import { createSlice, nanoid } from '@reduxjs/toolkit';
import type { Dispatch, PayloadAction } from '@reduxjs/toolkit';
import Flow, {
  Action,
  CatalogProductDetail,
  ConditionField,
  ImageAction,
  KeyboardInput,
  LogConversation,
  TempFile,
  VideoAction,
  Wa_List,
  WaFlowAction,
  WaTemplateAction,
  externalRequest,
} from 'apps/web/builder/types/flow';
import { applyEdgeChanges, applyNodeChanges } from 'reactflow';
import type { NodeChange, EdgeChange } from 'reactflow';

import { AppState } from '@/lib/store';

interface AddNodePayload {
  newNode: Flow.Node;
}

const initialState: Flow.State = {
  name: '',
  chatbot_id: '',
  channel: '',
  channelType: '',
  node: {
    id: '',
    type: '',
    position: {
      x: 0,
      y: 0,
    },
    data: {
      label: '',
      output: '',
      actions: [],
    },
  },
  action: {},
  nodes: [],
  edges: [],
  knowledges: [],
  isOpenSidebar: false,
  isOpenPreviewChat: false,
  isOpenPreviewChatV1: false,
  keyTrigger: 0,
  isOpenFloatingButton: false,
  uploadKeyTrigger: 0,
  removedNodes: [],
  isOpenModalNewUserField: false,
  isOpenPublish: false,
  keyTriggerCallback: 0,
  tempTitleNode: '',
  linkUrlSidebar: '',
  isOpenInactivityModal: false,
  reminders: undefined,
};

const flowSlice = createSlice({
  name: 'flow',
  initialState,
  reducers: {
    setInitialFlow: (state, action) => {
      state.edges = action.payload.edges;
      state.nodes = action.payload.nodes;
      state.knowledges = action.payload.knowledges;
      state.channel = action.payload.channel;
      state.channelType = action.payload.channelType;
      state.name = action.payload.name;
      state.chatbot_id = action.payload.chatbot_id;
      state.reminders = action.payload.reminders;
    },
    setValueAction: (
      state,
      action: PayloadAction<{
        message?: string;
        id: string;
        nodeid: string;
        tempFile?: TempFile;
        button?: string;
        image?: ImageAction;
        video?: VideoAction;
        keyboardInput?: KeyboardInput;
        logConversation?: LogConversation;
        externalRequest?: externalRequest;
        catalog_id?: string;
        catalogProductDetail?: CatalogProductDetail[];
        selected_set_id?: string;
        selected_set_name?: string;
        wa_list?: Wa_List;
        wa_flow?: WaFlowAction;
        wa_template?: WaTemplateAction;
        responseTime?: string;
        conditionField?: ConditionField[];
        liveAgentTarget?: string;
      }>
    ) => {
      const {
        message,
        id,
        nodeid,
        tempFile,
        button,
        image,
        video,
        keyboardInput,
        logConversation,
        externalRequest,
        catalog_id,
        catalogProductDetail,
        selected_set_id,
        selected_set_name,
        wa_list,
        wa_flow,
        wa_template,
        responseTime,
        conditionField,
        liveAgentTarget,
      } = action.payload;
      const nodeIndex = state.nodes.findIndex(
        (node) => node.data.label === nodeid
      );

      if (nodeIndex !== -1) {
        const nodesCopy = [...state.nodes];

        const actions = state.nodes[nodeIndex].data.actions.map((action) => {
          if (action.id === id) {
            const replaceAction = {
              ...action,
            };
            if (message) {
              replaceAction.message = {
                ...action.message,
                value: message,
              };
            }
            if (tempFile) {
              replaceAction.tempFile = {
                ...action.tempFile,
                fileName: tempFile.fileName,
                preview: tempFile.preview,
                rawFile: tempFile.rawFile,
              };
            }
            if (image) {
              replaceAction.image = {
                ...action.image,
                imageId: image.imageId,
                required: true,
                value: image.value,
              };
              replaceAction.tempFile = {
                ...action.tempFile,
                fileName: '',
                preview: '',
                rawFile: null,
              };
            }
            if (video) {
              replaceAction.video = {
                ...action.video,
                videoId: video.videoId,
                required: true,
                value: video.value,
              };
              replaceAction.tempFile = {
                ...action.tempFile,
                fileName: '',
                preview: '',
                rawFile: null,
              };
            }
            if (button) {
              replaceAction.buttons = [...action.buttons, button];
            }
            if (keyboardInput) {
              replaceAction.keyboardInput = {
                ...keyboardInput,
              };
            }
            if (logConversation) {
              replaceAction.logConversation = {
                ...logConversation,
                eventTitle: logConversation.eventTitle,
              };
            }
            // `!== undefined` (bukan truthy) supaya pilihan "Auto" (string kosong)
            // bisa mengosongkan target yang sebelumnya sudah dipilih.
            if (liveAgentTarget !== undefined) {
              replaceAction.liveAgentTarget = liveAgentTarget;
            }
            if (externalRequest) {
              replaceAction.externalRequest = {
                ...externalRequest,
              };
            }
            if (catalogProductDetail) {
              replaceAction.catalogProduct = {
                ...action.catalogProduct,
                catalog_id,
                product: catalogProductDetail,
              };
            }
            if (selected_set_id && selected_set_name) {
              replaceAction.catalogProduct = {
                ...action.catalogProduct,
                selected_set_id,
                selected_set_name,
              };
            }
            if (wa_list) {
              // Merge existing wa_list data properly
              replaceAction.wa_list = {
                ...replaceAction.wa_list,
                ...wa_list,
                media:
                  wa_list.media === null
                    ? null // allow removing media
                    : wa_list.media
                      ? {
                        // merge existing media with updated fields
                        ...replaceAction.wa_list?.media,
                        ...wa_list.media,
                        mediaId:
                          wa_list.media.mediaId ??
                          replaceAction.wa_list?.media?.mediaId ??
                          null,
                      }
                      : replaceAction.wa_list?.media,
                tempFile:
                  wa_list.tempFile === null
                    ? null
                    : wa_list.tempFile ??
                    replaceAction.wa_list?.tempFile ?? {
                      fileName: '',
                      preview: '',
                      rawFile: null,
                    },
              };

              // Maintain edge sync if list menu sections exist
              if (wa_list.sections && wa_list.sections.length > 0) {
                wa_list.sections.forEach((newSection) => {
                  newSection.rows.forEach((newRow) => {
                    const edgeIndex = state.edges.findIndex(
                      (edge) => edge.sourceHandle === newRow.id
                    );
                    if (edgeIndex !== -1) {
                      state.edges[edgeIndex] = {
                        ...state.edges[edgeIndex],
                        sourceHandle: newRow.id,
                      };
                    }
                  });
                });
              }
            }

            if (wa_flow) {
              replaceAction.wa_flow = {
                ...replaceAction.wa_flow,
                ...wa_flow,
              };
            }

            if (wa_template) {
              replaceAction.wa_template = {
                ...replaceAction.wa_template,
                ...wa_template,
              };
            }

            if (responseTime) {
              replaceAction.responseTime = responseTime;
            }
            if (conditionField) {
              replaceAction.conditionField = conditionField;
            }

            return replaceAction;
          }
          return action;
        });

        state.nodes[nodeIndex].data.actions = actions;
      }
    },
    selectAction: (
      state,
      action: PayloadAction<{
        nodeid: string;
        action: Flow.Action;
      }>
    ) => {
      state.action = {
        nodeid: action.payload.nodeid,
        ...action.payload.action,
      };
    },
    selectActions: (state, actions: PayloadAction<Flow.Action[]>) => {
      state.actions = actions.payload;
    },
    deselectAction: (state) => {
      state.action = null;
    },
    selectNode: (state, action: PayloadAction<Flow.Node>) => {
      state.node = action.payload;
    },
    selectNodes: (state, action: PayloadAction<Flow.Node[]>) => {
      state.nodes = action.payload;
    },
    deselectRemovedNodes: (state) => {
      state.removedNodes = [];
    },
    deselectNode: (state) => {
      state.node = null;
    },
    addNewAction: (
      state,
      action: PayloadAction<{ nodeLabel: string; newAction: Action }>
    ) => {
      const { nodeLabel, newAction } = action.payload;

      const node = state.nodes.find((value) => value.data.label === nodeLabel);

      if (node) {
        node.data.actions.push(newAction);
      }
    },
    reorderActions: (
      state,
      action: PayloadAction<{
        nodeLabel: string;
        sourceIndex: number;
        destinationIndex: number;
      }>
    ) => {
      const { nodeLabel, sourceIndex, destinationIndex } = action.payload;
      const node = state.nodes.find((value) => value.data.label === nodeLabel);

      if (node) {
        const { actions } = node.data;
        const reorderedActions = [...actions];
        const [removed] = reorderedActions.splice(sourceIndex, 1);
        reorderedActions.splice(destinationIndex, 0, removed);
        node.data.actions = reorderedActions;
      }
    },
    setNodes: (state, action: PayloadAction<NodeChange[]>) => {
      state.nodes = applyNodeChanges(
        action.payload,
        state.nodes
      ) as Flow.Node[];
    },
    setEdges: (state, action: PayloadAction<EdgeChange[]>) => {
      console.log(action.payload);
      state.edges = applyEdgeChanges(action.payload, state.edges);
    },
    onConnect: (state, action) => {
      state.edges.push(action.payload);
    },
    addDocument: (state) => {
      state.knowledges.push({
        type: 'document',
        documents: [],
      });
    },
    addWebsite: (state) => {
      state.knowledges.push({
        type: 'website',
        websites: [],
      });
    },
    addNode: (state, action: PayloadAction<AddNodePayload>) => {
      const { newNode } = action.payload;
      state.nodes.push(newNode);
    },
    setNode: (
      state,
      action: PayloadAction<{ nodeid: string; newName: string }>
    ) => {
      const { nodeid, newName } = action.payload;

      const nodeIndex = state.nodes.findIndex((node) => node.id === nodeid);

      if (nodeIndex !== -1) {
        state.nodes[nodeIndex].data.output = newName;
      }
    },
    removeNode: (state, action: PayloadAction<{ nodeId: string }>) => {
      const { nodeId } = action.payload;

      const node = state.nodes.find((value) => value.id === nodeId);

      if (node) {
        const indexOf = state.nodes.findIndex((value) => value.id === nodeId);
        state.nodes[indexOf].data.actions.forEach((action) => {
          if (action.name === 'Image' || action.name === 'Video') {
            if (action?.name === 'Image') {
              if (action.image.imageId !== -1) {
                state.removedNodes.push(state.nodes[indexOf]);
              }
            } else if (action?.name === 'Video') {
              if (action.video.videoId !== -1) {
                state.removedNodes.push(state.nodes[indexOf]);
              }
            }
          }
        });

        state.nodes.splice(indexOf, 1);
      }
    },
    addEdge: (state, action) => {
      state.edges.push({ id: nanoid(), ...action.payload });
    },
    setIsOpenSidebar: (state, action) => {
      state.isOpenSidebar = action.payload;
    },
    setIsOpenPreviewChat: (state, action) => {
      state.isOpenPreviewChat = action.payload;
    },
    setIsOpenPreviewChatV1: (state, action) => {
      state.isOpenPreviewChatV1 = action.payload;
    },
    changeNode: (state, action: PayloadAction<Flow.Node>) => {
      const { id, type, position, data } = action.payload;
      const nodeIndex = state.nodes.findIndex((node) => node.id === id);
      if (nodeIndex !== -1) {
        state.nodes[nodeIndex].type = type;
        state.nodes[nodeIndex].position = position;
        state.nodes[nodeIndex].data = data;
      }
    },
    removeAction: (
      state,
      action: PayloadAction<{ nodeLabel: string; actionId: string }>
    ) => {
      const { nodeLabel, actionId } = action.payload;

      const node = state.nodes.find((value) => value.data.label === nodeLabel);

      if (node) {
        const indexOf = state.node.data.actions.findIndex(
          (value) => value.id === actionId
        );

        node.data.actions.splice(indexOf, 1);
      }
    },
    setKeyTrigger: (state, action) => {
      state.keyTrigger = action.payload + 1;
    },
    removeEdge: (state, action) => {
      const edgeIndex = state.edges.findIndex(
        (edge) => edge.id === action.payload
      );
      if (edgeIndex !== -1) {
        state.edges.splice(edgeIndex, 1);
      }
    },
    setIsOpenFloatingButton: (state, action) => {
      state.isOpenFloatingButton = action.payload;
    },
    setUploadKeyTrigger: (state, action) => {
      state.uploadKeyTrigger = action.payload + 1;
    },
    setIsOpenModalNewUserField: (state, action) => {
      state.isOpenModalNewUserField = action.payload;
    },
    removeConditionFieldAtribute: (
      state,
      action: PayloadAction<{
        nodeId: string;
        conditionFieldIndex: number;
        atributesIndex: number;
      }>
    ) => {
      const { nodeId, conditionFieldIndex, atributesIndex } = action.payload;

      const node = state.nodes.find((node) => node.data.label === nodeId);

      if (node) {
        node.data.actions[0].conditionField[
          conditionFieldIndex
        ].atributes.splice(atributesIndex, 1);
      }
    },
    setConditionField: (
      state,
      action: PayloadAction<{
        nodeId: string;
        isAdd: boolean;
        conditionFieldId: string;
        conditionFieldIndex?: number;
      }>
    ) => {
      const { nodeId, isAdd, conditionFieldIndex } = action.payload;

      const node = state.nodes.find((node) => node.data.label === nodeId);

      if (node) {
        if (isAdd) {
          node.data.actions[0].conditionField.push({
            conditionFieldId: `${nanoid(10)}`,
            contactMatch: 'all of the following conditions?',
            atributes: [],
          });
        } else {
          node.data.actions[0].conditionField.splice(conditionFieldIndex, 1);
        }
      }
    },
    setIsOpenPublish: (state, action) => {
      state.isOpenPublish = action.payload;
    },
    selectEdges: (state, action) => {
      state.edges = action.payload;
    },
    setTempTitleNode: (state, action) => {
      state.tempTitleNode = action.payload;
    },
    setLinkUrlSidebar: (state, action) => {
      state.linkUrlSidebar = action.payload;
    },
    setIsOpenInactivityModal: (state, action) => {
      state.isOpenInactivityModal = action.payload;
    },
    setReminders: (state, action) => {
      state.reminders = action.payload;
    },
  },
});

export const {
  onConnect,
  setInitialFlow,
  setValueAction,
  setNodes,
  setEdges,
  addNode,
  addEdge,
  selectAction,
  deselectAction,
  selectNode,
  deselectNode,
  addNewAction,
  reorderActions,
  addDocument,
  addWebsite,
  setIsOpenSidebar,
  setIsOpenPreviewChat,
  changeNode,
  removeAction,
  setKeyTrigger,
  setUploadKeyTrigger,
  removeNode,
  removeEdge,
  setIsOpenFloatingButton,
  setNode,
  selectNodes,
  selectActions,
  deselectRemovedNodes,
  setIsOpenModalNewUserField,
  removeConditionFieldAtribute,
  setConditionField,
  setIsOpenPublish,
  selectEdges,
  setTempTitleNode,
  setIsOpenPreviewChatV1,
  setLinkUrlSidebar,
  setIsOpenInactivityModal,
  setReminders,
} = flowSlice.actions;

export const selectFlow = (state: AppState) => state.flow;

export default flowSlice.reducer;
